import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchAllData } from "./quiz3Utils";
import { computeAnswerSheetLayout, MARKER_BOX_MM, PAGE_EDGE_MARGIN_MM } from "./omrLayout";
import markerImg from "./assets/omr_marker.jpg";
import { getHsUid } from "../happyschool_static/hsAuth";

const CONTENT_INSET_MM = PAGE_EDGE_MARGIN_MM + MARKER_BOX_MM / 2;

// Phiếu tô ô quét ảnh (OMR) chỉ đủ chỗ cho ~70 câu trên 1 trang A4 (dòng cao
// tối thiểu = 1 ô tròn). Đề dài hơn (vd đề thi hết môn 200 câu) -> in phiếu
// trả lời dạng BẢNG (5 cột x 40 câu/trang, không quét ảnh được) và in đề bài
// 2 cột chữ nhỏ cho đỡ tốn giấy.
const OMR_MAX_QUESTIONS = 70;
const PLAIN_COLS = 5;
const PLAIN_ROWS = 40;

// PQ-01 — in đề ra giấy dạng "Đề bài" (đọc câu hỏi bình thường) + trang
// "Phiếu trả lời" riêng (lưới ô tròn đều, có 4 dấu marker góc để OMRChecker
// tự canh thẳng khi quét ảnh chụp). Chỉ hỗ trợ đề loại "fixed" vì phiếu
// giấy cần thứ tự câu cố định, biết trước giữa lúc in và lúc chấm.
const Quiz3PrintSheet = () => {
  const { quizId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [topicName, setTopicName] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchAllData()
      .then((data) => {
        if (cancelled) return;
        const q = (data.quizzes || {})[quizId];
        if (!q) { setError("Không tìm thấy đề thi này."); setLoading(false); return; }
        if (q.closed && q.createdByUid !== getHsUid()) { setError("Đề này đang đóng."); setLoading(false); return; }
        if (q.type !== "fixed") {
          setError("Chỉ in được phiếu trả lời với đề CỐ ĐỊNH (fixed) — đề tổng hợp có thứ tự câu ngẫu nhiên mỗi lần nên không in được.");
          setLoading(false);
          return;
        }
        const ids = q.questionIds || [];
        const qs = ids.map((id) => ((data.questions || {})[id] ? { id, ...data.questions[id] } : null)).filter(Boolean);
        if (qs.length === 0) { setError("Đề này chưa có câu hỏi nào."); setLoading(false); return; }
        setQuiz({ id: quizId, ...q });
        setQuestions(qs);
        setTopicName(q.topicId ? ((data.topics || {})[q.topicId]?.name || "") : "");
        setLoading(false);
      })
      .catch((err) => { if (!cancelled) { setError(err.message || "Có lỗi khi tải đề thi."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [quizId]);

  const layout = useMemo(() => computeAnswerSheetLayout(Math.max(questions.length, 1)), [questions.length]);
  const isLong = questions.length > OMR_MAX_QUESTIONS;
  // Phiếu dạng bảng: chia thành từng trang PLAIN_COLS x PLAIN_ROWS câu.
  const plainPages = useMemo(() => {
    const perPage = PLAIN_COLS * PLAIN_ROWS;
    const pages = [];
    for (let start = 0; start < questions.length; start += perPage) {
      const cols = [];
      for (let c = 0; c < PLAIN_COLS; c++) {
        const from = start + c * PLAIN_ROWS;
        const to = Math.min(from + PLAIN_ROWS, questions.length, start + perPage);
        if (from < to) cols.push(Array.from({ length: to - from }, (_, i) => from + i + 1));
      }
      pages.push(cols);
    }
    return pages;
  }, [questions.length]);

  if (loading) return <div style={pageWrap}>Đang tải đề thi...</div>;
  if (error) {
    return (
      <div style={pageWrap}>
        <p style={{ color: "#dc2626" }}>{error}</p>
        <Link to="/quiz3/take">← Quay lại chọn đề</Link>
      </div>
    );
  }

  // Nhóm số thứ tự bubble đầu tiên của mỗi cột (để vẽ header "A  B  C  D").
  const colHeaderRows = [];
  const seenCol = new Set();
  layout.rows.forEach((row) => {
    if (!seenCol.has(row.col)) { seenCol.add(row.col); colHeaderRows.push(row); }
  });

  return (
    <div>
      <style>{PRINT_CSS}</style>

      <div className="no-print" style={toolbarStyle}>
        <button onClick={() => window.print()} style={primaryBtnStyle}>🖨 In phiếu trả lời</button>
        <Link to="/quiz3/take" style={secondaryBtnStyle}>← Quay lại chọn đề</Link>
        <p style={{ color: "#6b7280", fontSize: 13, margin: "10px 0 0" }}>
          {isLong ? (
            <>Đề dài {questions.length} câu: phiếu trả lời in dạng bảng (khoanh tròn đáp án), giáo viên chấm tay theo trang "In đáp án" — không quét ảnh được. Chọn khổ A4, tỉ lệ 100%.</>
          ) : (
            <>Mẹo khi in: chọn khổ giấy A4, tỉ lệ 100% (không "Vừa trang"/Fit to page), tắt lề tự do (margin: none) để 4 dấu tròn ở góc trang "Phiếu trả lời" in đúng vị trí.
            Sau khi học sinh làm bài xong, chỉ cần chụp ảnh <b>trang "Phiếu trả lời"</b> (thấy rõ đủ 4 dấu tròn góc) để nộp bằng ảnh.</>
          )}
        </p>
      </div>

      <div id="quiz3-print-area">
        {/* ----- Trang Đề bài ----- */}
        <div className={`quiz3-print-page${isLong ? " quiz3-long" : ""}`}>
          <h2 style={{ textAlign: "center", margin: "0 0 4px" }}>{quiz.title}</h2>
          {quiz.timeLimitSeconds ? (
            <p style={{ textAlign: "center", margin: "0 0 6px" }}>Thời gian làm bài: {Math.round(quiz.timeLimitSeconds / 60)} phút · {questions.length} câu</p>
          ) : null}
          {topicName && <p style={{ textAlign: "center", color: "#6b7280", margin: "0 0 16px" }}>{topicName}</p>}
          <p style={{ margin: "0 0 16px" }}>
            Họ và tên: ____________________________________&nbsp;&nbsp;&nbsp; Lớp: ______________
          </p>
          <ol className="quiz3-question-list" style={{ paddingLeft: 20, margin: 0 }}>
            {questions.map((q) => (
              <li key={q.id} style={{ marginBottom: isLong ? 6 : 12 }}>
                <p style={{ fontWeight: 600, margin: "0 0 4px" }}>{q.content}</p>
                {(q.options || []).map((opt, i) => (
                  <p key={opt.id} style={{ margin: "2px 0 2px 14px" }}>
                    {String.fromCharCode(65 + i)}. {opt.text}
                  </p>
                ))}
              </li>
            ))}
          </ol>
        </div>

        {/* ----- Phiếu trả lời dạng bảng cho đề dài ----- */}
        {isLong && plainPages.map((cols, pi) => (
          <div key={`plain-${pi}`} className="quiz3-print-page quiz3-plain-sheet">
            <p style={{ textAlign: "center", fontWeight: 700, margin: "0 0 2px", fontSize: 15 }}>
              PHIẾU TRẢ LỜI{plainPages.length > 1 ? ` (trang ${pi + 1}/${plainPages.length})` : ""}
            </p>
            <p style={{ textAlign: "center", margin: "0 0 6px", fontSize: 11, color: "#4b5563" }}>{quiz.title}</p>
            <p style={{ fontSize: 11, margin: "0 0 4px" }}>
              Họ và tên: ______________________________&nbsp;&nbsp; Lớp: ____________&nbsp;&nbsp; Điểm: ________
            </p>
            <p style={{ fontSize: 9.5, color: "#6b7280", margin: "0 0 6px" }}>Khoanh tròn 1 chữ cái cho mỗi câu. Muốn sửa: gạch chéo đáp án cũ rồi khoanh đáp án mới.</p>
            <div className="quiz3-plain-grid">
              {cols.map((nums, ci) => (
                <div key={ci} className="quiz3-plain-col">
                  {nums.map((n) => (
                    <div key={n} className="quiz3-plain-row">
                      <span className="quiz3-plain-num">{n}.</span>
                      {["A", "B", "C", "D"].map((L) => <span key={L} className="quiz3-plain-opt">{L}</span>)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* ----- Trang Phiếu trả lời (chỉ cần chụp ảnh trang này) ----- */}
        {!isLong && (
        <div className="quiz3-print-page quiz3-answer-page">
          <img className="quiz3-marker quiz3-marker-tl" src={markerImg} alt="" />
          <img className="quiz3-marker quiz3-marker-tr" src={markerImg} alt="" />
          <img className="quiz3-marker quiz3-marker-bl" src={markerImg} alt="" />
          <img className="quiz3-marker quiz3-marker-br" src={markerImg} alt="" />

          <div className="quiz3-answer-content">
            <p style={{ textAlign: "center", fontWeight: 700, margin: "0 0 2px", fontSize: 15 }}>PHIẾU TRẢ LỜI</p>
            <p style={{ textAlign: "center", margin: "0 0 8px", fontSize: 11, color: "#4b5563" }}>{quiz.title}</p>
            <p style={{ fontSize: 10.5, margin: "0 0 2px" }}>
              Họ và tên: __________________________&nbsp;&nbsp; Lớp: ____________&nbsp;&nbsp; Mã đề: ________
            </p>
            <p style={{ fontSize: 9, color: "#6b7280", margin: 0 }}>
              Dùng bút chì tô KÍN 1 ô tròn cho mỗi câu. Không tô lem ra ngoài, không tô nhiều hơn 1 ô/câu.
            </p>

            <div className="quiz3-bubble-area">
              {colHeaderRows.map((row) => (
                <div
                  key={`hdr-${row.col}`}
                  className="quiz3-col-header"
                  style={{ left: `${row.numberLeftPct}%`, top: `${row.numberTopPct - 3.2}%` }}
                >
                  Câu&nbsp;&nbsp;&nbsp;&nbsp;A&nbsp;&nbsp;B&nbsp;&nbsp;C&nbsp;&nbsp;D
                </div>
              ))}

              {layout.rows.map((row) => (
                <React.Fragment key={row.questionNumber}>
                  <div
                    className="quiz3-q-number"
                    style={{ left: `${row.numberLeftPct}%`, top: `${row.numberTopPct}%` }}
                  >
                    {row.questionNumber}
                  </div>
                  {row.bubbles.map((b) => (
                    <div
                      key={b.value}
                      className="quiz3-bubble"
                      style={{
                        left: `${b.leftPct}%`,
                        top: `${b.topPct}%`,
                        width: `${layout.bubbleWidthPct}%`,
                        height: `${layout.bubbleHeightPct}%`,
                      }}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

const PRINT_CSS = `
@page { size: A4; margin: 0; }
.quiz3-print-page {
  width: 210mm;
  min-height: 297mm;
  box-sizing: border-box;
  padding: 14mm;
  margin: 0 auto 24px;
  background: #fff;
  page-break-after: always;
}
.quiz3-print-page.quiz3-answer-page {
  padding: 0;
  height: 297mm;
  position: relative;
  overflow: hidden;
}
.quiz3-marker { position: absolute; width: ${MARKER_BOX_MM}mm; height: ${MARKER_BOX_MM}mm; }
.quiz3-marker-tl { top: ${PAGE_EDGE_MARGIN_MM}mm; left: ${PAGE_EDGE_MARGIN_MM}mm; }
.quiz3-marker-tr { top: ${PAGE_EDGE_MARGIN_MM}mm; right: ${PAGE_EDGE_MARGIN_MM}mm; }
.quiz3-marker-bl { bottom: ${PAGE_EDGE_MARGIN_MM}mm; left: ${PAGE_EDGE_MARGIN_MM}mm; }
.quiz3-marker-br { bottom: ${PAGE_EDGE_MARGIN_MM}mm; right: ${PAGE_EDGE_MARGIN_MM}mm; }
.quiz3-answer-content {
  position: absolute;
  top: ${CONTENT_INSET_MM}mm;
  left: ${CONTENT_INSET_MM}mm;
  right: ${CONTENT_INSET_MM}mm;
  bottom: ${CONTENT_INSET_MM}mm;
}
.quiz3-long { font-size: 11.5px; }
.quiz3-long .quiz3-question-list { column-count: 2; column-gap: 8mm; }
.quiz3-long .quiz3-question-list li { break-inside: avoid; page-break-inside: avoid; }
.quiz3-plain-sheet { padding: 12mm 12mm 10mm; }
.quiz3-plain-grid { display: flex; justify-content: space-between; border-top: 1px solid #000; padding-top: 4px; }
.quiz3-plain-col { flex: 1; padding: 0 2mm; border-right: 1px dashed #9ca3af; }
.quiz3-plain-col:last-child { border-right: none; }
.quiz3-plain-row { display: flex; align-items: center; height: 5.5mm; font-size: 10px; }
.quiz3-plain-num { width: 8mm; font-weight: 600; text-align: right; margin-right: 1.5mm; }
.quiz3-plain-opt { display: inline-flex; align-items: center; justify-content: center; width: 4.2mm; height: 4.2mm; border: 1px solid #374151; border-radius: 50%; font-size: 8px; margin-right: 1.2mm; }
.quiz3-bubble-area { position: relative; width: 100%; height: 100%; }
.quiz3-bubble {
  position: absolute;
  border: 1.4px solid #000;
  border-radius: 50%;
  background: #fff;
}
.quiz3-q-number {
  position: absolute;
  transform: translateY(-50%);
  font-size: 8.5px;
  font-weight: 600;
}
.quiz3-col-header {
  position: absolute;
  font-size: 7.5px;
  color: #6b7280;
}
@media screen {
  #quiz3-print-area { background: #e5e7eb; padding: 20px 0; }
  .quiz3-print-page { box-shadow: 0 1px 4px rgba(0,0,0,0.2); }
}
@media print {
  body * { visibility: hidden; }
  #quiz3-print-area, #quiz3-print-area * { visibility: visible; }
  #quiz3-print-area { position: absolute; left: 0; top: 0; width: 100%; background: #fff; padding: 0; }
  .no-print { display: none !important; }
  .quiz3-print-page { box-shadow: none; margin: 0; }
}
`;

const pageWrap = { padding: 24, maxWidth: 800, margin: "0 auto" };

const toolbarStyle = { padding: "16px 24px", maxWidth: 800, margin: "0 auto" };

const primaryBtnStyle = {
  padding: "8px 18px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  marginRight: 10,
};

const secondaryBtnStyle = {
  padding: "8px 18px",
  background: "#fff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: "pointer",
  textDecoration: "none",
  fontWeight: 600,
  display: "inline-block",
};

export default Quiz3PrintSheet;
